import { api } from '@goaltree/backend/convex/_generated/api'
import { COACH_CHARACTERS, type CoachCharacter } from '@goaltree/backend/convex/lib/coach'
import { cn } from '@goaltree/ui/lib/utils'
import { useAction, useQuery } from 'convex/react'
import {
  CheckIcon,
  CookieIcon,
  FingerprintIcon,
  FlaskConicalIcon,
  LeafIcon,
  LockIcon,
  type LucideIcon,
  MegaphoneIcon,
  PlayIcon,
  SquareIcon,
  TrophyIcon,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useIsPro } from '@/hooks/use-is-pro'

export const CHARACTER_ICONS: Record<CoachCharacter, LucideIcon> = {
  drill: MegaphoneIcon,
  hypeMachine: TrophyIcon,
  scientist: FlaskConicalIcon,
  zen: LeafIcon,
  noir: FingerprintIcon,
  grandma: CookieIcon,
}

type Clip = { audio: string; contentType: string }
const sampleClips = new Map<string, Promise<Clip>>()

// Sample lines alternate per character. Pro users hear them in their coach's
// voice at the character's speed (server-cached, quota-exempt); free users read them.
function useCharacterSample(prefetch: boolean) {
  const { t, i18n } = useTranslation()
  const getSample = useAction(api.voice.getCharacterSample)
  const prefs = useQuery(api.preferences.getMyPreferences) as { coachVoice?: string } | undefined
  const voice = prefs?.coachVoice
  const locale = i18n.language
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const tokenRef = useRef(0)
  const turnRef = useRef<Record<string, number>>({})
  const [playing, setPlaying] = useState<string | null>(null)
  const [shown, setShown] = useState<{ id: string; text: string } | null>(null)

  function stop() {
    tokenRef.current += 1
    audioRef.current?.pause()
    audioRef.current = null
    setPlaying(null)
  }
  useEffect(() => stop, [])

  function load(id: CoachCharacter, variant: 'a' | 'b') {
    const key = `${voice ?? ''}:${id}:${variant}:${locale}`
    let p = sampleClips.get(key)
    if (!p) {
      p = getSample({ voice, character: id, variant, locale })
      p.catch(() => sampleClips.delete(key))
      sampleClips.set(key, p)
    }
    return p
  }

  useEffect(() => {
    if (prefetch && prefs) for (const c of COACH_CHARACTERS) void load(c.id, 'a').catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefetch, !!prefs, voice, locale])

  async function play(id: CoachCharacter, audible: boolean) {
    if (playing === id) return stop()
    stop()
    const n = (turnRef.current[id] = (turnRef.current[id] ?? -1) + 1)
    const variant = n % 2 ? 'b' : 'a'
    const text = t(`coach.sample.${id}.${variant}`)
    setShown({ id, text })
    if (!audible) return
    const token = tokenRef.current
    setPlaying(id)
    try {
      const clip = await load(id, variant)
      if (token !== tokenRef.current) return
      const el = new Audio(`data:${clip.contentType};base64,${clip.audio}`)
      audioRef.current = el
      const done = () => token === tokenRef.current && setPlaying(null)
      el.onended = done
      el.onerror = done
      await el.play()
    } catch {
      if (token === tokenRef.current) setPlaying(null)
    }
  }

  return { playing, shown, play, stop }
}

export function CoachCharacterList({
  pinned,
  onPick,
  onLocked,
  compact,
  row,
}: {
  pinned: string | undefined
  onPick: (id: CoachCharacter) => void
  onLocked: () => void
  compact?: boolean
  row?: boolean
}) {
  const { t } = useTranslation()
  const { isPro } = useIsPro()
  const sample = useCharacterSample(isPro)

  if (row) {
    const hintId = sample.shown?.id ?? pinned
    const hint = COACH_CHARACTERS.find((c) => c.id === hintId)
    return (
      <div className="flex flex-col gap-1.5">
        <div className="flex gap-2 overflow-x-auto px-3 py-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {COACH_CHARACTERS.map((c) => {
            const Icon = CHARACTER_ICONS[c.id]
            const locked = c.pro && !isPro
            const on = pinned === c.id
            const playing = sample.playing === c.id
            return (
              <div
                key={c.id}
                className={cn(
                  'flex w-[104px] shrink-0 flex-col overflow-hidden rounded-xl border',
                  on ? 'border-primary bg-primary/10' : 'border-border/60',
                )}
              >
                <button
                  type="button"
                  aria-pressed={on}
                  onClick={() => (locked ? onLocked() : onPick(c.id))}
                  className="flex flex-1 cursor-pointer flex-col items-center gap-1.5 px-1.5 pt-2.5 pb-2 text-center hover:bg-muted/40"
                >
                  <span
                    className={cn(
                      'flex h-9 w-9 items-center justify-center rounded-full',
                      on ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground',
                    )}
                  >
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="flex min-h-[2lh] items-center gap-1 text-[12px] font-semibold leading-tight text-balance text-foreground">
                    {t(`coach.style.${c.id}`)}
                    {locked && <LockIcon className="h-3 w-3 shrink-0 text-muted-foreground" />}
                  </span>
                </button>
                <button
                  type="button"
                  aria-label={t('coach.playSample')}
                  onClick={() => void sample.play(c.id, isPro)}
                  className={cn(
                    'flex cursor-pointer items-center justify-center gap-1 border-t py-1.5 text-[11px] font-semibold transition-colors',
                    on ? 'border-primary/30' : 'border-border/60',
                    playing
                      ? 'text-primary-text'
                      : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground',
                  )}
                >
                  {playing ? (
                    <SquareIcon className="h-2.5 w-2.5 fill-current" />
                  ) : (
                    <PlayIcon className="h-3 w-3 fill-current" />
                  )}
                  {t(playing ? 'coach.stopSample' : 'coach.hearSample')}
                </button>
              </div>
            )
          })}
        </div>
        {hint && (
          <p className="px-3 text-[11px] leading-snug text-muted-foreground">
            {sample.shown?.id === hint.id ? (
              <em>“{sample.shown.text}”</em>
            ) : (
              t(`coach.toneHint.${hint.id}`)
            )}
          </p>
        )}
      </div>
    )
  }

  return (
    <div className={cn('flex flex-col', compact ? 'gap-0.5' : 'gap-1')}>
      {COACH_CHARACTERS.map((c) => {
        const Icon = CHARACTER_ICONS[c.id]
        const locked = c.pro && !isPro
        const on = pinned === c.id
        return (
          <div
            key={c.id}
            className={cn(
              'flex items-start gap-2.5 rounded-lg px-2 py-1.5',
              on ? 'bg-primary/10' : 'hover:bg-muted/50',
            )}
          >
            <button
              type="button"
              onClick={() => (locked ? onLocked() : onPick(c.id))}
              className="flex min-w-0 flex-1 cursor-pointer items-start gap-2.5 text-left"
            >
              <span
                className={cn(
                  'mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md',
                  on ? 'bg-primary/15 text-primary-text' : 'bg-muted text-muted-foreground',
                )}
              >
                <Icon className="h-3.5 w-3.5" />
              </span>
              <span className="min-w-0">
                <span className="flex items-center gap-1 text-[13px] font-semibold text-foreground">
                  {t(`coach.style.${c.id}`)}
                  {locked && <LockIcon className="h-3 w-3 text-muted-foreground" />}
                  {on && <CheckIcon className="h-3.5 w-3.5 text-primary-text" />}
                </span>
                <span className="block text-[11px] leading-snug text-muted-foreground">
                  {sample.shown?.id === c.id ? (
                    <em>“{sample.shown.text}”</em>
                  ) : (
                    t(`coach.toneHint.${c.id}`)
                  )}
                </span>
              </span>
            </button>
            <button
              type="button"
              title={t('coach.playSample')}
              aria-label={t('coach.playSample')}
              onClick={() => void sample.play(c.id, isPro)}
              className="mt-0.5 flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              {sample.playing === c.id ? (
                <SquareIcon className="h-3 w-3 fill-current" />
              ) : (
                <PlayIcon className="h-3.5 w-3.5 fill-current" />
              )}
            </button>
          </div>
        )
      })}
    </div>
  )
}
